import axios from 'axios';
import { logout } from '../utils';

type Auth0Client = {
  getAccessTokenSilently: () => Promise<string>;
  logout?: (...args: any[]) => any;
};

let _auth0: Auth0Client | null = null;
let _interceptorRegistered = false;

const AUTH_PROVIDER_HEADER = 'bagel-auth-provider';
const CLIENT_ID_HEADER = 'bagel-client-id';
const AUTH0_PROVIDER = 'auth0';

const isBagelApiRequest = ({ url, baseURL }: { url?: string; baseURL?: string }): boolean => {
  const apiDomain = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN;
  if (!apiDomain || !url) return false;
  try {
    return new URL(url, baseURL ?? window.location.origin).origin === new URL(apiDomain).origin;
  } catch {
    return false;
  }
};

/**
 * Wire the global axios request interceptor to the current Auth0 instance.
 * Call synchronously during render, as early as possible in the component that
 * owns the Auth0Provider tree (e.g. the top of App.tsx, before any hook that may
 * fetch on mount) — idempotent, so safe to call on every render. Calling it from
 * a useEffect instead lets sibling hooks whose own mount-time fetch effects run
 * first (React runs effects in hook-declaration order) send requests before
 * `_auth0` is set, so they go out with no Authorization header.
 */
export function setupAuthInterceptor(auth0: Auth0Client): void {
  _auth0 = auth0;
  registerAuthInterceptor();
}

/**
 * Registers the interceptor as soon as this module is imported, so requests
 * fired before `setupAuthInterceptor(auth0)` runs (e.g. from `Api.ts`, which
 * no longer builds its own auth headers) still go through it — it's a no-op
 * until `_auth0` is set. `setupAuthInterceptor` also calls this, guarded by
 * `_interceptorRegistered`, in case it runs first.
 */
registerAuthInterceptor();

export function registerAuthInterceptor(): void {
  if (_interceptorRegistered) return;
  _interceptorRegistered = true;

  axios.interceptors.request.use(async (config) => {
    if (!_auth0) return config;
    const existingAuth =
      config.headers?.Authorization ?? (config.headers as any)?.authorization;
    if (existingAuth) return config;

    try {
      const token = await _auth0.getAccessTokenSilently();
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
      if (isBagelApiRequest(config)) {
        config.headers[AUTH_PROVIDER_HEADER] = AUTH0_PROVIDER;
        config.headers[CLIENT_ID_HEADER] = window.__RUNTIME_CONFIG__.REACT_APP_AUTH0_CLIENT_ID;
      }
    } catch (e: any) {
      // Session cap reached: the refresh token expired / the session is gone.
      // Log the user out instead of surfacing an error mid-request.
      const code = e?.error ?? '';
      const sessionExpired =
        code === 'missing_refresh_token' ||
        code === 'login_required' ||
        code === 'invalid_grant' ||
        /missing refresh token|login required/i.test(e?.message ?? '');

      if (sessionExpired) {
        if (typeof _auth0.logout === 'function') {
          logout(_auth0 as any);
        } else {
          localStorage.clear();
          window.location.assign(window.location.origin);
        }
        // Abort the in-flight request — the page is navigating away
        return Promise.reject(new axios.Cancel('Session expired'));
      }
      throw e;
    }

    return config;
  });
}
